import React, { FunctionComponent } from 'react'
import { Transfer } from 'antd'
import type { TransferProps } from 'antd'
import { useLoaderData, useMatch } from '@tanstack/react-router'
import { arrayToMap, getAllData } from '../../../../../utils/data'
import { formatTimestamp } from '../../../../../utils/time'
import { Vehicle } from 'server'
import { useQuery } from '@tanstack/react-query'
import { useFeathersClient } from '@strac/react-client-feathers'
import { TransferKey } from 'antd/es/transfer/interface'

interface RecordType {
  key: number
  title: string
  description: string
}

interface IProps {
  targetKeys: number[]
  setTargetKeys: (value: TransferKey[]) => void
  selectedKeys: number[]
  setSelectedKeys: (value: TransferKey[]) => void
}

const CollectionsTableTransfer: FunctionComponent<any> = (props: IProps) => {
  const { targetKeys, setTargetKeys, selectedKeys, setSelectedKeys } = props

  const match = useMatch({ strict: false })
  const { vehicles, agencies, vehicleLocations } = useLoaderData({ from: match.routeId ?? '/' })

  const client = useFeathersClient()

  const missingVehicleIds = targetKeys
    .filter((vehicleId) => !vehicles.find((vehicle: Vehicle) => vehicle.vehicle_id === vehicleId))
    .map((vehicleId) => vehicleId)
  const missingVehicleAgencyIds = vehicles.map((vehicle: Vehicle) => vehicle.agency_id)

  const missingVehicleQuery = {
    vehicle_id: {
      $in: missingVehicleIds
    }
  }
  const { data: missingVehicleData } = useQuery({
    queryKey: ['vehicle', 'find', missingVehicleQuery],
    queryFn: () => getAllData(client, 'vehicle', missingVehicleQuery),
    enabled: !!missingVehicleIds.length,
    placeholderData: []
  })
  const { data: missingVehicleLocationData } = useQuery({
    queryKey: ['vehicle-location', 'find', missingVehicleQuery],
    queryFn: () => getAllData(client, 'vehicle-location', missingVehicleQuery),
    enabled: !!missingVehicleIds.length,
    placeholderData: []
  })
  const missingVehicleAgencyQueries = {
    id: {
      $in: missingVehicleAgencyIds
    }
  }
  const { data: missingVehicleAgencyData } = useQuery({
    queryKey: ['agencies', 'find', missingVehicleAgencyQueries],
    queryFn: () => getAllData(client, 'agencies', missingVehicleAgencyQueries),
    enabled: !!missingVehicleAgencyIds.length,
    placeholderData: []
  })

  const onChange: TransferProps['onChange'] = (nextTargetKeys, direction, moveKeys) => {
    setTargetKeys(nextTargetKeys)
  }

  const onSelectChange: TransferProps['onSelectChange'] = (sourceSelectedKeys, targetSelectedKeys) => {
    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys])
  }

  const agenciesMap = arrayToMap([...agencies, ...missingVehicleAgencyData], 'id')
  const vehicleLocationMap = arrayToMap([...vehicleLocations, ...missingVehicleLocationData], 'vehicle_id')

  const customVehicleSort = (a: Vehicle, b: Vehicle) => {
    if (a.name < b.name) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    return 0
  }

  const data: RecordType[] = [...vehicles, ...missingVehicleData]
    .sort(customVehicleSort)
    .map((vehicle: any): RecordType => {
      const agency = agenciesMap.get(vehicle.agency_id)
      const vehicleLocation = vehicleLocationMap.get(vehicle.vehicle_id)
      return {
        key: vehicle.vehicle_id,
        title: `${vehicle.name} ${agency ? `(${agency?.name})` : ''}`,
        description: `${agency ? `${agency?.name} - ` : ''}${
          vehicleLocation ? `${formatTimestamp(vehicleLocation.update_received)} ` : ''
        }`
      }
    })

  return (
    <Transfer
      style={{
        flex: 1
      }}
      listStyle={{
        width: '100%',
        height: '60vh',
        minHeight: '500px'
      }}
      showSearch={true}
      dataSource={data}
      titles={['Source', 'Target']}
      targetKeys={targetKeys}
      selectedKeys={selectedKeys}
      onChange={onChange}
      onSelectChange={onSelectChange}
      render={(item: RecordType) => item.title}
    />
  )
}

export default CollectionsTableTransfer
